import { createVisitorSchema } from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiList } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  Notice,
  Pager,
  errorMessage,
  primaryButton,
  secondaryButton,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t } from '../../i18n';
import { type Message, type Person } from './shared';

interface Visitor {
  id: string;
  studentName: string;
  visitorName: string;
  relationship: string | null;
  enteredAt: string;
  leftAt: string | null;
}

const blank = { name: '', relationship: '', phone: '' };

export function VisitorsTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const recording = can('hostel.visitors', schoolId);
  const [form, setForm] = useState(blank);
  const [search, setSearch] = useState('');
  const [person, setPerson] = useState<Person | null>(null);
  const [insideOnly, setInsideOnly] = useState(true);
  const [page, setPage] = useState(1);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<Message>(null);

  const matches = useQuery({
    queryKey: ['hostel', schoolId, 'students', search],
    queryFn: () =>
      apiList<Person>('/students', { query: { schoolId, q: search, pageSize: 6 } }).then(
        (r) => r.data,
      ),
    enabled: recording && search.trim().length >= 2 && !person,
  });
  const visitors = useQuery({
    queryKey: ['hostel', schoolId, 'visitors', insideOnly, page],
    queryFn: () =>
      apiList<Visitor>(`/schools/${schoolId}/hostel/visitors`, {
        query: { insideOnly: insideOnly ? 'true' : undefined, page, pageSize: 20 },
      }),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['hostel', schoolId] });
  const fail = (e: unknown) => setMessage({ kind: 'error', text: errorMessage(e) });
  const signIn = useMutation({
    mutationFn: (body: object) =>
      api(`/schools/${schoolId}/hostel/visitors`, { method: 'POST', body }),
    onSuccess: () => {
      setForm(blank);
      setPerson(null);
      setSearch('');
      setMessage({ kind: 'ok', text: t('ho.visitorIn') });
      refresh();
    },
    onError: fail,
  });
  const signOut = useMutation({
    mutationFn: (id: string) =>
      api(`/schools/${schoolId}/hostel/visitors/${id}/leave`, { method: 'POST' }),
    onSuccess: () => {
      setMessage(null);
      refresh();
    },
    onError: fail,
  });

  return (
    <section aria-label={t('ho.tab.visitors')} className="space-y-6">
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      {recording && (
        <form
          noValidate
          aria-label={t('ho.signIn')}
          className="grid items-end gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-4"
          onSubmit={(e) => {
            e.preventDefault();
            const parsed = createVisitorSchema.safeParse({
              studentId: person?.id,
              visitorName: form.name,
              relationship: form.relationship,
              phone: form.phone,
            });
            const next: Record<string, string> = {};
            if (!parsed.success)
              for (const i of parsed.error.issues) next[String(i.path[0])] = i.message;
            setErrors(next);
            if (!parsed.success) return;
            setMessage(null);
            signIn.mutate(parsed.data);
          }}
        >
          <div className="relative">
            <TextField
              label={t('ho.visiting')}
              value={person ? `${person.firstName} ${person.lastName}` : search}
              error={errors.studentId ? t('ho.chooseStudent') : undefined}
              onChange={(e) => {
                setPerson(null);
                setSearch(e.target.value);
              }}
            />
            {!person && matches.data && matches.data.length > 0 && (
              <ul className="absolute z-10 mt-1 w-full divide-y divide-slate-100 rounded border border-slate-200 bg-white text-sm shadow">
                {matches.data.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      className="w-full p-2 text-left hover:bg-slate-50"
                      onClick={() => setPerson(p)}
                    >
                      {p.firstName} {p.lastName} · {p.admissionNumber}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <TextField
            label={t('ho.visitorName')}
            value={form.name}
            error={errors.visitorName}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            label={t('ho.relationship')}
            value={form.relationship}
            error={errors.relationship}
            onChange={(e) => setForm({ ...form, relationship: e.target.value })}
          />
          <TextField
            label={t('ho.phone')}
            value={form.phone}
            error={errors.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
          <div className="sm:col-span-4">
            <button type="submit" className={primaryButton} disabled={signIn.isPending}>
              {t('ho.signIn')}
            </button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">{t('ho.visitorBook')}</h2>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={insideOnly}
              onChange={(e) => {
                setInsideOnly(e.target.checked);
                setPage(1);
              }}
            />
            {t('ho.insideOnly')}
          </label>
        </div>
        {visitors.data?.data.length === 0 && <p className="text-slate-600">{t('ho.noVisitors')}</p>}
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
          {visitors.data?.data.map((v) => (
            <li key={v.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
              <span>
                <span className="font-medium">{v.visitorName}</span>
                {v.relationship ? ` (${v.relationship})` : ''} → {v.studentName} ·{' '}
                {new Date(v.enteredAt).toLocaleString()}
                {v.leftAt ? ` – ${new Date(v.leftAt).toLocaleTimeString()}` : ''}
              </span>
              {recording && !v.leftAt && (
                <button
                  type="button"
                  className={secondaryButton}
                  aria-label={`${t('ho.signOut')}: ${v.visitorName}`}
                  disabled={signOut.isPending}
                  onClick={() => signOut.mutate(v.id)}
                >
                  {t('ho.signOut')}
                </button>
              )}
            </li>
          ))}
        </ul>
        {visitors.data && visitors.data.meta.totalPages > 1 && (
          <Pager page={page} totalPages={visitors.data.meta.totalPages} onPage={setPage} />
        )}
      </div>
    </section>
  );
}
